import React, { useState, useRef, useCallback, useEffect } from "react";
import SVG from "src/utils/SVG";
import styled from "styled-components";
import VideoBox from "./VideoBox";
import IconButton from "./IconButton";
import imageCompression from "browser-image-compression";
import Button from "./Button";
import VideoPlayer from "react-video-thumbnail";

const imageServerIp = process.env.REACT_APP_IMAGE_SERVER_IP;
interface IInputFileProps {
  className?: string;
  imageList: (File | Image)[];
  setImageList: (files: (File | Image)[]) => void;
  accept?: string;
}
const compressionOption = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1920,
  initialQuality: 0.7,
};
const InputFileV2 = (props: IInputFileProps) => {
  const [imageList, setImageList] = useState<(File | Image)[]>(props.imageList);
  useEffect(() => {
    setImageList(props.imageList);
  }, [props.imageList]);
  useEffect(() => {
    props.setImageList(imageList);
  }, [imageList]);

  const saveFileImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectFiles = e.target.files;
    if (selectFiles !== null) {
      try {
        if (selectFiles.length > 0) {
          if (selectFiles.length > 31 || imageList.length > 31) {
            alert("최대 파일 개수는 30개 입니다.");
            return;
          }
          let totalFileSize = 0;
          for (let i = 0; i < selectFiles.length; i++) {
            totalFileSize += Number(selectFiles[i].size);
            const fileType = selectFiles[i].type;
            const oneFileSize = Number(selectFiles[i].size / 1024);
            // 이미지 로직
            if (String(fileType).indexOf("image") > -1) {
              if (oneFileSize > 10000) {
                // 이미지 파일
                alert("하나의 사진 파일이 10MB보다 클 수 없습니다.");
                return;
              }
            } else if (String(fileType).indexOf("video") > -1) {
              if (oneFileSize > 30000) {
                // 동영상 파일
                alert("하나의 동영상 파일이 30MB보다 클 수 없습니다.");
                return;
              }
            }
            if (totalFileSize / 1024 > 150000) {
              alert("전체 파일 사이즈가 150MB를 넘길 수 없습니다.");
              return;
            }
          }
          for (let i = 0; i < selectFiles.length; i++) {
            const file = selectFiles[i];
            if (String(file.type).includes("image") == true) {
              const compressedFile = await imageCompression(file, compressionOption);
              const newFile = new File([compressedFile], compressedFile.name, { type: file.type });
              setImageList((images) => [...images, newFile]);
            } else {
              setImageList((images) => [...images, file]);
            }
          }
        }
      } catch (error) {
        alert("이미지 업로드 실패 다시 시도해 주세요");
        console.error("imageUpload Error", error);
      }
    }
  };

  // 파일 삭제
  const deleteFileImage = (selectedFile: File) => {
    const removedList = imageList.filter((image) => image !== selectedFile);
    setImageList(removedList);
  };

  return (
    <InputFileSet>
      <label htmlFor="basic-file">파일업로드</label>
      <input
        id="basic-file"
        type="file"
        accept={props.accept ? props.accept : "image/*,video/*,.mov,.mp4"}
        name="profile_img"
        onChange={saveFileImage}
        multiple
      />
      {imageList.length > 0 && (
        <PreviewImg>
          {imageList.map((image: File | Image, index: number) => {
            return (image as File).name ? (
              <li key={index}>
                <div>
                  {(image as File).type.includes("image") === true ? (
                    <img alt="sample" src={URL.createObjectURL(image as File)} />
                  ) : (
                    <VideoPlayer videoUrl={URL.createObjectURL(image as File)} snapshotAt={1} height={70} width={70} renderThumbnail={true} />
                  )}
                </div>
                <IconButton btnName="미리보기 삭제하기" onClick={() => deleteFileImage(image as File)}>
                  <SVG name="thumbDel" width={24} height={24} viewBox="0 0 24 24" />
                </IconButton>
              </li>
            ) : (
              <li key={index}>
                <div>
                  {(image as Image).img_url.includes("attachment") === true ? (
                    <img alt="sample" src={imageServerIp + (image as Image).img_url} />
                  ) : (
                    <VideoBox vimeoId={(image as Image).img_url} />
                  )}
                </div>
                <IconButton btnName="미리보기 삭제하기" onClick={() => deleteFileImage(image as File)}>
                  <SVG name="thumbDel" width={24} height={24} viewBox="0 0 24 24" />
                </IconButton>
              </li>
            );
          })}
        </PreviewImg>
      )}
    </InputFileSet>
  );
};

const InputFileSet = styled.div`
  ${({ theme }) => theme.flexBox.flexMixin("row", "flex-start", "flex-start")}
  padding-top: 20px;
  > label {
    position: relative;
    flex-basis: 72px;
    display: inline-block;
    width: 72px;
    height: 72px;
    margin: 3px 3px 0 0;
    border: 1px dashed ${({ theme }) => theme.color.lineSecondary};
    border-radius: 4px;
    font-size: 0;
    &::before {
      position: absolute;
      top: 50%;
      left: 50%;
      content: "";
      display: block;
      width: 1px;
      height: 11px;
      margin-top: -5px;
      background-color: ${({ theme }) => theme.color.lineSecondary};
    }
    &::after {
      position: absolute;
      top: 50%;
      left: 50%;
      content: "";
      display: block;
      width: 11px;
      height: 1px;
      margin-left: -5px;
      background-color: ${({ theme }) => theme.color.lineSecondary};
    }
  }
  > input[type="file"] {
    /* 파일 필드 숨기기 */
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }
`;

const PreviewImg = styled.ul`
  overflow: hidden;
  overflow-x: scroll;
  ${({ theme }) => theme.flexBox.flexMixin("row", "flex-start", "flex-start")}
  flex: 1;
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
  > li {
    position: relative;
    padding: 3px 3px 0 0;
    > div {
      overflow: hidden;
      position: relative;
      width: 72px;
      height: 72px;
      border: 1px solid #ddd;
      border-radius: 4px;
      > img {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        display: block;
        object-fit: cover;
        max-width: 100%;
        height: auto;
        box-sizing: border-box;
      }
    }
    > button {
      z-index: 900;
      position: absolute;
      top: 0;
      right: 0;
      width: 24px;
      height: 24px;
    }
  }
`;

export default InputFileV2;
